// S-3050: SwingRun types and selectors for Cloudflare-aware Library cards

export interface SwingRun {
  id: string;
  user_id: string;
  status: 'uploaded' | 'processing' | 'ready' | 'failed';
  cf_stream_uid?: string | null;
  cf_playback_url?: string | null;
  original_filename?: string | null;
  legacy_video_id?: string | null; // S-3011: Link to legacy Mux video
  pose_data?: {
    meta?: {
      durationSec?: number;
      width?: number;
      height?: number;
    };
  } | null;
  ai_result?: {
    meta?: {
      durationSec?: number;
    };
  } | null;
  analysis_data?: any; // JSONB: Analysis Agent output (analysis_v2 schema)
  analysis_enriched?: {
    swing_quality?: number;
    peer_label?: string;
    phase_summaries?: Array<{
      observations?: Array<{
        flaw_id?: string;
        impact_tier?: string;
        impact_potential?: string;
      }>;
    }>;
  } | null; // JSONB: Enriched analysis with taxonomy mapping and scoring
  coaching_result?: any; // JSONB: Coaching Agent output (coaching_v2 schema)
  failure_reason?: string | null;
  created_at: string;
  updated_at: string;
  schema_version?: string | null;
}

export type SwingRunState = 'processing' | 'ready' | 'failed';

/**
 * Get display filename for a swing run
 * Prefers original_filename, falls back to CF uid from playback URL
 */
export function getDisplayFilename(run: SwingRun): string {
  // Prefer original_filename
  if (run.original_filename) {
    return run.original_filename;
  }

  // Extract uid from cf_playback_url
  if (run.cf_playback_url) {
    // Match 32-char hex uid from Cloudflare URLs
    // Example: https://customer-xxx.cloudflarestream.com/7c11454a2f500013f8b88a91200a134b/manifest/video.m3u8
    const match = run.cf_playback_url.match(/\/([a-f0-9]{32})\//i);
    if (match) {
      const uid = match[1];
      return `${uid.substring(0, 8)}...`;
    }
  }

  // Fallback to uid if available
  if (run.cf_stream_uid) {
    return `${run.cf_stream_uid.substring(0, 8)}...`;
  }

  return 'Untitled';
}

/**
 * Get duration in seconds from pose_data or ai_result
 * Returns null if not available
 */
export function getDurationSec(run: SwingRun): number | null {
  // Prefer pose_data.meta.durationSec (most accurate from video)
  const poseDuration = run.pose_data?.meta?.durationSec;
  if (typeof poseDuration === 'number' && poseDuration > 0) {
    return poseDuration;
  }

  // Fallback to ai_result.meta.durationSec
  const aiDuration = run.ai_result?.meta?.durationSec;
  if (typeof aiDuration === 'number' && aiDuration > 0) {
    return aiDuration;
  }

  return null;
}

/**
 * Format duration in seconds to mm:ss
 */
export function formatDuration(durationSec: number | null): string {
  if (!durationSec || durationSec <= 0) {
    return '';
  }

  const minutes = Math.floor(durationSec / 60);
  const seconds = Math.floor(durationSec % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Get simplified state for UI rendering
 */
export function getState(run: SwingRun): SwingRunState {
  if (run.status === 'failed') {
    return 'failed';
  }

  if (run.status === 'ready') {
    return 'ready';
  }

  // 'uploaded' and 'processing' both map to 'processing'
  return 'processing';
}

/**
 * Check if run is playable
 */
export function isPlayable(run: SwingRun): boolean {
  return run.status === 'ready' && !!run.cf_playback_url;
}

/**
 * Check if run can be retried
 */
export function isRetryable(run: SwingRun): boolean {
  return run.status === 'failed';
}

